import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { captureAndCleanUrl } from "@/lib/utm";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import Products from "@/pages/Products";
import PosSystems from "@/pages/PosSystems";
import MobileDevices from "@/pages/MobileDevices";
import Ecommerce from "@/pages/Ecommerce";
import CashDiscount from "@/pages/CashDiscount";
import FreePos from "@/pages/FreePos";
import Industries from "@/pages/Industries";
import Partnerships from "@/pages/Partnerships";
import Resources from "@/pages/Resources";
import BlogArticle from "@/pages/BlogArticle";
import FAQ from "@/pages/FAQ";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import SavingsAnalysis from "@/pages/SavingsAnalysis";
import GetStarted from "@/pages/GetStarted";
import Syracuse from "@/pages/Syracuse";
import IndustryPage from "@/pages/IndustryPage";
import FreePosFunnel from "@/pages/FreePosFunnel";
import CashDiscountFunnel from "@/pages/CashDiscountFunnel";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminLeadDetail from "@/pages/AdminLeadDetail";
import AuthCallback from "@/pages/AuthCallback";
import NotFound from "@/pages/NotFound";
import { AuthProvider } from "@/contexts/AuthContext";

function AppRouter() {
  const location = useLocation();
  // Capture & clean UTM/tracking params on every navigation. Idempotent.
  useEffect(() => { captureAndCleanUrl(); }, [location.pathname]);
  // Synchronous detection of Google OAuth callback fragment (must run before normal routes mount).
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Standalone ad funnels — no site nav/footer */}
      <Route path="/free-pos-offer" element={<FreePosFunnel />} />
      <Route path="/cash-discount-savings" element={<CashDiscountFunnel />} />

      {/* Admin — protected, no public nav */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/admin/leads/:id" element={<AdminLeadDetail />} />

      {/* OAuth callback fallback for bare visits (no hash) — the hash-based path
          is handled above by the early-return check. Rendering AuthCallback here
          guarantees /auth/callback never falls through to Layout+NotFound,
          which would leak Organization JSON-LD and the wrong robots directive. */}
      <Route path="/auth/callback" element={<AuthCallback />} />

      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/services" element={<Services />} />
        <Route path="/products" element={<Products />} />
        <Route path="/pos-systems" element={<PosSystems />} />
        <Route path="/mobile-devices" element={<MobileDevices />} />
        <Route path="/e-commerce" element={<Ecommerce />} />
        <Route path="/cash-discount" element={<CashDiscount />} />
        <Route path="/free-pos-program" element={<FreePos />} />
        <Route path="/industries" element={<Industries />} />
        <Route path="/partnerships" element={<Partnerships />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/resources/faq" element={<FAQ />} />
        <Route path="/resources/:slug" element={<BlogArticle />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/savings-analysis" element={<SavingsAnalysis />} />
        <Route path="/get-started" element={<GetStarted />} />
        <Route path="/merchant-services-syracuse-ny" element={<Syracuse />} />
        <Route path="/industries/:slug" element={<IndustryPage />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        {/* Explicit theme="light" avoids sonner's matchMedia-based initial
            state, which caused a hydration mismatch on prerendered pages. */}
        <Toaster richColors position="top-right" theme="light" />
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}
